import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../../../services/subscription';
import type { OrganizationSubscription } from '../../../models/subscription';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

/**
 * The current organization's BASE subscription.
 *
 * Keyed by organizationId, and that is load-bearing. The request itself is scoped by the
 * `X-Organization-ID` header, so an unkeyed cache silently serves one organization's
 * subscription while the app is showing another. With a paywall reading this, that meant
 * paying for one organization made every other one look active — banner gone, buttons
 * enabled — for the whole staleTime, even though the server had only ever changed one.
 */
export const useSubscription = () => {
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );

  return useQuery<OrganizationSubscription | null, Error>({
    queryKey: ['organizationSubscription', organizationId],
    queryFn: () => subscriptionService.getSubscription(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: !!organizationId,
  });
};
